import { Injectable } from "@angular/core";
import { BadgeControllerService } from "@booking/angular-booking-generic-service";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { TIME_OUT_REQUEST } from "@shared/constant";
import { BadgeName } from "@shared/enum/badge-enum";
import { finalize, timeout } from "rxjs";
import { BillingService } from "./billing.service";

@Injectable({
  providedIn: 'root'
})

export class BadgeService {

  badgeConfigMap: Record<string, any> = {};
  currentLang: string = '';

  constructor(
    private badgeControllerService: BadgeControllerService,
    private translateService: TranslateService,
    private billingService: BillingService
    
  ) {
    this.currentLang = this.translateService.currentLang || 'vi';
    const badgeCache = JSON.parse(localStorage.getItem('badge-config') || '{}');
    const badgeList = badgeCache.data || [];
    
    this.buildBadgeMap(badgeList);
    this.preloadExchangeRates();
    this.getBadge();
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLang = this.translateService.currentLang || 'vi';
    });
  }

  private getBadge() {
    this.badgeControllerService
      .getBadges()
      .pipe(timeout(TIME_OUT_REQUEST), finalize(() => {}))
      .subscribe((res: any) => {
        if (res && res.success) {
          const badgeList = res.data || [];
          this.buildBadgeMap(badgeList);
          this.preloadExchangeRates();
          const now = new Date().getTime();
          localStorage.setItem(
            'badge-config',
            JSON.stringify({ data: badgeList, timestamp: now })
          );
        }
      });
  }

  private buildBadgeMap(badgeList: any[]) {
    this.badgeConfigMap = {};
    for (const badge of badgeList) {
      if (badge.name) {
        this.badgeConfigMap[badge.name] = badge;
      }
    }
  }

  getBadgeLabel(badgeCode: BadgeName, originPrice: number = 0, netPrice: number = 0): string | null {
    const badge = this.badgeConfigMap[badgeCode];
    
    if (!badge) return null;
  
    switch (badgeCode) {
      case BadgeName.SPECIAL_DISCOUNT:
        return this.getBadgeTextByPrice(badge, originPrice, netPrice);
      default:
        return this.currentLang === 'vi' ? badge.vi : badge.en;
    }
  }

  getBadgeTextByPrice(badge: any, originPrice: number = 0, netPrice: number = 0): string | null {
    if (!originPrice || !netPrice || netPrice >= originPrice) return null;
    
    const currency = badge.currency;
    const rate = currency && currency !== 'USD'
      ? this.exchangeRateMap[currency] || 1
      : 1;
    const discountAmountInUSD = badge.discount_price_amount * rate;

    const discountAmount = originPrice - netPrice;
    const discountPercent = (discountAmount / originPrice) * 100;
  
    const isValidAmount = badge.discount_price_amount && discountAmount >= discountAmountInUSD;
    const isValidPercent = badge.discount_price_percent && discountPercent >= badge.discount_price_percent;
  
    if (isValidAmount || isValidPercent) {
      return this.currentLang === 'vi' ? badge.vi : badge.en;
    }
  
    return null;
  }

  private exchangeRateMap: { [currency: string]: number } = {};

  private preloadExchangeRates() {
    const currencies = new Set<string>();
    const now = Date.now();

    for (const key in this.badgeConfigMap) {
      const badge = this.badgeConfigMap[key];
      const currency = badge?.currency;
      if (currency && currency !== 'USD') {
        currencies.add(currency);
      }
    }
    currencies.forEach((currency) => {
      const cacheKey = `exchange-rate-${currency}-USD`;
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.expiry_timestamp > now) {
            this.exchangeRateMap[currency] = parsed.exchange_rate;
            return;
          }
        } catch (_) {}
      }

      this.billingService.getExchangeRateV2(currency, 'USD').subscribe({
        next: (res) => {
          if (res) {
            this.exchangeRateMap[currency] = res;
          }
        },
        error: () => {
          console.warn(`Failed to load exchange rate for ${currency}`);
        }
      });
    });
  }

}